import { FC, useState } from 'react'
import {
  Text,
  GreyGrey80,
  Primary13Regular,
  SequenceMenu,
  useNotifications,
  SimpleModal,
  Flex
} from '@sequencehq/core-components'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { useActivateAutomationVersion } from 'Automations/automationEditor/hooks'
import { DuplicateTriggerWarningBanner } from './DuplicateTriggerWarningBanner'
import { useDuplicateActiveTriggerCheck } from './useDuplicateActiveTriggerCheck'

type TriggerType = Dashboardv99990101Api.Automations.TriggerType

type Props = {
  automationId: string
  versionId: string
  triggerType: TriggerType
  onSuccess?: () => void
}

export const PublishWorkflowMenuItem: FC<Props> = ({
  automationId,
  versionId,
  triggerType,
  onSuccess
}) => {
  const { displayNotification } = useNotifications()
  const [showPublishModal, setShowPublishModal] = useState<boolean>(false)

  const { isLoading, conflict } = useDuplicateActiveTriggerCheck({
    automationId,
    triggerType
  })

  const activateVersion = useActivateAutomationVersion()

  return (
    <>
      <SequenceMenu.Item
        key="publish-workflow"
        uuid="publish-workflow"
        label="Publish workflow"
        data-testid="automations.editor.kebabMenu.publish"
        isDisabled={isLoading}
        onClick={() => setShowPublishModal(true)}
      />
      <SimpleModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        variant="dialog"
        title="Publish workflow"
        submitButtonText="Publish"
        submittingButtonText="Publishing..."
        onSubmit={async () => {
          try {
            await activateVersion({ automationId, versionId })
            displayNotification('Workflow published', { type: 'success' })
            onSuccess?.()
            return true
          } catch (error) {
            console.error(error)
            displayNotification('Could not publish workflow', { type: 'error' })
            return false
          }
        }}
      >
        <Flex flexDir="column" gap="16px">
          {conflict && (
            <DuplicateTriggerWarningBanner
              conflictingAutomationId={conflict.automationId}
              conflictingAutomationName={conflict.automationName}
              triggerType={triggerType}
            />
          )}
          <Text {...Primary13Regular} color={GreyGrey80}>
            Are you sure you want to publish this workflow?
          </Text>
        </Flex>
      </SimpleModal>
    </>
  )
}
